<template>
    <div ref="profileAreaRef" class="relative mt-auto px-4 pt-4 -mb-2">
        <div
            class="overflow-hidden rounded-(--border-radius-md) border border-(--white-alpha-08) bg-(image:--button-dark-surface) shadow-[-0.125rem_-0.188rem_0.25rem_-0.063rem_rgba(5,5,5,0.18),0_-0.063rem_0.188rem_0_rgba(5,5,5,0.08),0.063rem_0.063rem_0.125rem_0_rgba(5,5,5,0.08),0_-0.063rem_0.125rem_0_rgba(5,5,5,0.06),0_0.063rem_0.063rem_0_rgba(5,5,5,0.18),0_0.063rem_0.125rem_0_rgba(5,5,5,0.1),0.063rem_0.125rem_0.188rem_0_rgba(5,5,5,0.06),inset_0_0.063rem_0_rgba(255,255,255,0.06)]">
            <Motion
                as="button"
                :while-press="SidebarUserProfileMotion.Press"
                class="group flex w-full cursor-pointer items-center gap-3 border-none px-3.5 py-2.5 font-inherit transition-colors duration-150 ease-out hover:bg-(--white-alpha-04)"
                @click="toggleProfile">
                <div class="relative h-9.5 w-9.5 shrink-0">
                    <UserAvatar
                        ref="userAvatarRef"
                        :user-id="userSessionStore.userId"
                        :full-name="userSessionStore.userInfo?.name!"
                        :alt="userSessionStore.userInfo?.name || ''"
                        size-class="size-full rounded-[10.063rem] border-2 border-(--white-alpha-12) bg-[#1e3a8a] shadow-[0_0.125rem_0.5rem_rgba(0,0,0,0.3)]"
                        text-class="text-white" />
                </div>

                <div class="flex min-w-0 flex-1 flex-col overflow-hidden">
                    <span
                        class="overflow-hidden text-ellipsis whitespace-nowrap text-[0.8125rem] leading-tight font-semibold text-slate-100">
                        {{ userSessionStore.userInfo?.name }}
                    </span>
                    <span class="text-[0.6875rem] font-bold uppercase tracking-[0.06em] text-slate-500">
                        {{ userSessionStore.userInfo?.role[0] }}
                    </span>
                </div>
                <Motion
                    as="span"
                    :animate="profileMotionState"
                    :transition="SidebarNavigationMotion.ChevronTransition"
                    :variants="SIDEBAR_CHEVRON_VARIANTS"
                    :class="[
                        'flex w-6 shrink-0 items-center justify-center text-slate-500 transition-colors duration-150 ease-[ease] group-hover:text-slate-400',
                        isProfileOpen ? 'text-slate-400' : ''
                    ]">
                    <Icon name="br-icon:chevron-up" size="9" />
                </Motion>
            </Motion>
            <Motion
                :animate="profileMotionState"
                :initial="false"
                :transition="DRAWER_TRANSITION"
                :variants="DRAWER_HEIGHT_VARIANTS"
                class="overflow-hidden">
                <div
                    class="relative m-[0_0.375rem_0.375rem] flex flex-col gap-0.5 rounded-[0.625rem] bg-[rgba(0,0,0,0.15)] p-[0.25rem_0.5rem_0.5rem_0.5rem] shadow-[inset_0.125rem_0.125rem_0.25rem_rgba(0,0,0,0.2),inset_-0.063rem_-0.063rem_0.188rem_rgba(255,255,255,0.03)]">
                    <SidebarUserProfileMenuItem
                        v-for="(item, index) in sidebarUserProfileNavigationItems"
                        :key="item.label"
                        :item="item"
                        :index="index"
                        :state="profileMotionState" />
                    <div
                        class="mx-2 my-0.5 h-px bg-[linear-gradient(to_right,transparent,var(--white-alpha-06),transparent)]"></div>
                    <Motion
                        as="button"
                        :animate="profileMotionState"
                        :initial="false"
                        :transition="signOutTransition"
                        :variants="SidebarProfileSignOutVariants"
                        class="flex h-9.5 w-full cursor-pointer items-center gap-2 rounded-[0.625rem] border border-transparent bg-transparent px-2 font-inherit text-[0.8rem] font-medium text-slate-300 transition-colors duration-150 ease-out hover:bg-(--danger-alpha-10) hover:text-(--text-field-error)"
                        @click="signOut">
                        <div
                            class="flex h-6.5 w-6.5 shrink-0 items-center justify-center rounded-[0.438rem] bg-[rgba(239,68,68,0.12)] text-(--text-field-error) shadow-(--sidebar-menu-icon-inset-shadow)">
                            <Icon name="br-icon:log-out" size="14" />
                        </div>
                        <span>Sign out</span>
                    </Motion>
                </div>
            </Motion>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { onClickOutside } from '@vueuse/core';
    import type { Variant, Transition } from 'motion-v';

    import { sidebarUserProfileNavigationItems } from '@constants/navigation-items';
    import {
        DRAWER_HEIGHT_VARIANTS,
        SIDEBAR_CHEVRON_VARIANTS,
        DRAWER_TRANSITION,
        SidebarNavigationMotion,
        SidebarUserProfileMotion
    } from '@constants/motion';
    import { Opacity } from '@constants/motion/defaults';

    import { UIState, type UIStateType } from '@/constants/ui/ui-state';

    const SidebarProfileSignOutVariants = Object.freeze({
        [UIState.Collapsed]: { opacity: Opacity.Hidden, x: -8 },
        [UIState.Expanded]: { opacity: Opacity.Visible, x: 0 }
    } satisfies Record<UIStateType, Variant>);

    const auth = useAuth();
    const userSessionStore = useUserSessionStore();

    const isProfileOpen = ref(false);
    const profileAreaRef = useTemplateRef('profileAreaRef');
    const userAvatarRef = useTemplateRef('userAvatarRef');

    const profileMotionState = computed<UIStateType>(() =>
        isProfileOpen.value ? UIState.Expanded : UIState.Collapsed
    );

    const signOutTransition = computed(
        () =>
            ({
                ...SidebarUserProfileMotion.SignOutTransition,
                delay: isProfileOpen.value ? SidebarUserProfileMotion.SignOutOpenDelay : 0
            }) satisfies Transition
    );

    watch(
        () => userSessionStore.userInfo,
        async (userInfo) => {
            if (userInfo === undefined) {
                return;
            }

            await userAvatarRef.value?.refresh();
        }
    );

    function toggleProfile() {
        isProfileOpen.value = !isProfileOpen.value;
    }

    function signOut() {
        auth.signOut();
    }

    onClickOutside(profileAreaRef, () => {
        isProfileOpen.value = false;
    });
</script>
